/* eslint-disable @next/next/no-before-interactive-script-outside-document */
import "../styles/globals.css"
import "@fortawesome/fontawesome-svg-core/styles.css"

import { datadogLogs } from "@datadog/browser-logs"
import { config } from "@fortawesome/fontawesome-svg-core"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { NextPage } from "next"
import { IBM_Plex_Sans } from "next/font/google"
import Router, { useRouter } from "next/router"
import Script from "next/script"
import {
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useState,
} from "react"
import { Provider } from "react-redux"
import { PersistGate } from "redux-persist/integration/react"
import { UUID } from "shared-ts/classes/uuid"
import { LoggerStrategy, setLoggerStrategy } from "shared-ts/logger"
import { LoadingSpinner } from "shared-web/components/base/feedback/LoadingSpinner"
import {
  ModalContextProvider,
  useIsAnyKnownModalOpen,
} from "shared-web/components/base/overlay/Modal/ModalContext"
import { SidePanelContextProvider } from "shared-web/components/base/overlay/SidePanel"
import useKeyboardShortcut from "shared-web/hooks/useKeyboardShortcut"
import { toast } from "shared-web/lib/toast"

import { useAppDispatch, useInterval } from "app/hooks"
import { PreviousRouteContextProvider } from "app/PreviousRouteContext"
import { GIFT_CARD_SCAN_CAPTURE_SELECTOR } from "components/composite/GiftCardInput"
import { isApiError } from "features/api/apiSlice"
import { useLazyGetCompanyForSubdomainQuery } from "features/api/company"
import { useLazyGetScannedSaleQuery } from "features/api/sale"
import SessionTimeoutModal from "features/auth/SessionTimeoutModal"
import { SessionTimeoutSignIn } from "features/auth/SessionTimeoutSignIn"
import { getCurrentSubdomain } from "features/auth/useGetCompanyForCurrentSubdomain"
import { FAN_BUILDER_COUPON_ARMED_SELECTOR } from "features/pos/fanBuilderConstants"
import { SaleStatusFormatted } from "features/pos/sales/helpers"
import { setColorScheme } from "features/theme/themeSlice"
import { useBarcodeScanner } from "hooks/useBarcodeScanner"
import { useSession } from "hooks/useSession"
import { ErrorPage } from "layouts/components/ErrorPage"
import ErrorBoundary from "layouts/components/ErrorPage/ErrorBoundary"
import { Cookies } from "lib/cookies/cookies"
import { initializeDatadog } from "lib/datadog/datadog"

import store, { persistor } from "../app/store"

import type { AppProps } from "next/app"

config.autoAddCss = false

export type NextPageWithLayout<P = object, IP = P> = NextPage<P, IP> & {
  getLayout?: (page: ReactElement) => ReactNode
}

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout
}

const sessionExtendingEventTypes = [
  "click",
  "scroll",
  "keypress",
  "mousemove",
  "mousedown",
  "load",
]

initializeDatadog()

const WebLoggerStrategy: LoggerStrategy = {
  log(level, message, context, error) {
    datadogLogs.logger[level](message, context, error)
  },
}

const ibmPlexSans = IBM_Plex_Sans({
  subsets: ["latin"],
})

function App({ Component, pageProps }: AppPropsWithLayout) {
  const dispatch = useAppDispatch()
  const [isTimedOutModalOpen, setIsTimedOutModalOpen] = useState<boolean>(false)

  const router = useRouter()

  useEffect(() => {
    if (process.env.NEXT_PUBLIC_VERCEL_ENV === "production") {
      setLoggerStrategy(WebLoggerStrategy)
    }
  }, [])

  const [
    getCompanyForSubdomain,
    {
      error: getCompanyForSubdomainError,
      originalArgs: getCompanyForSubdomainArgs,
    },
  ] = useLazyGetCompanyForSubdomainQuery()

  const {
    isRevokingSession,
    isExtendingSession,
    throttledExtendSession,
    isSessionTimeoutModalOpen,
    signOutAndCloseSessionTimeoutModal,
    extendSessionAndCloseSessionTimeoutModal,
    extendSessionError,
    revokeSessionError,
  } = useSession()

  useEffect(() => {
    getCompanyForSubdomain(getCurrentSubdomain(), true)
  }, [getCompanyForSubdomain])

  useEffect(() => {
    dispatch(
      setColorScheme(
        document.documentElement.classList.contains("dark") ? "dark" : "light"
      )
    )
  }, [dispatch])

  useEffect(() => {
    document.documentElement.classList.add(ibmPlexSans.className)
  }, [])

  useInterval(
    () => {
      const { sessionExpiresDate } = Cookies.fromDocument(document)

      const routeRequiresAuth = router.pathname.split("/")[1] !== "auth"

      if (routeRequiresAuth && !sessionExpiresDate) {
        if (!isTimedOutModalOpen) {
          setIsTimedOutModalOpen(true)
        }
      } else {
        setIsTimedOutModalOpen(false)
      }
    },
    isSessionTimeoutModalOpen || isTimedOutModalOpen ? 500 : 10000
  )

  useEffect(() => {
    for (const eventType of sessionExtendingEventTypes) {
      document.addEventListener(eventType, throttledExtendSession)
    }

    return () => {
      for (const eventType of sessionExtendingEventTypes) {
        document.removeEventListener(eventType, throttledExtendSession)
      }
    }
  }, [throttledExtendSession])

  useKeyboardShortcut(
    "Escape",
    (event) => {
      if (event.target instanceof HTMLElement) {
        if (
          event?.target?.tagName === "INPUT" ||
          event?.target?.tagName === "SELECT" ||
          event?.target?.tagName === "TEXTAREA"
        ) {
          event?.target.blur()
        } else if (router.pathname.startsWith("/auth")) {
          setIsTimedOutModalOpen(false)
          router.push("/auth/sign-in")
        }
      }
    },
    {
      preventDefault: false,
      enableOnFormTags: true,
    }
  )

  const [getScannedSale] = useLazyGetScannedSaleQuery()

  const onScan = useCallback(
    async (code: string) => {
      if (!code.endsWith("-S")) {
        return
      }

      const { sessionExpiresDate } = Cookies.fromDocument(document)
      if (!sessionExpiresDate) {
        return
      }

      try {
        const { sale } = await getScannedSale({ identifier: code }).unwrap()
        if (sale?.id) {
          const url = `/pos/sales/${UUID.fromPB(sale.id).toString()}?status=${
            sale?.status &&
            SaleStatusFormatted.get(sale.status)
              ?.toLowerCase()
              .replace(" ", "-")
          }`
          Router.push(url)
        }
      } catch (error: unknown) {
        toast.error({
          message: `Sale ${code} not found`,
          error,
        })
      }
    },
    [getScannedSale]
  )

  const isAnyKnownModalOpen = useIsAnyKnownModalOpen()
  useBarcodeScanner({
    onScan,
    disabled: isAnyKnownModalOpen,
    ignorePipeScanWhenFocusWithin: `${FAN_BUILDER_COUPON_ARMED_SELECTOR}, ${GIFT_CARD_SCAN_CAPTURE_SELECTOR}`,
  })

  const getLayout = Component.getLayout ?? ((page) => page)

  if (
    isApiError(getCompanyForSubdomainError) &&
    getCompanyForSubdomainError.code === "not_found"
  ) {
    return (
      <ErrorPage
        message={`Subdomain ${getCompanyForSubdomainArgs} not found.`}
      />
    )
  }

  if (!router.isReady) {
    // When router isn't ready, we can't access url parameters from the Next.js router.
    // Next.js should be able to get to a ready state _very quickly_ on first render, so
    // blocking app rendering globally on it shouldn't break our user experience.
    // see https://nextjs.org/docs/pages/building-your-application/rendering/automatic-static-optimization
    return (
      <div className="flex h-screen">
        <div className="m-auto">
          <LoadingSpinner size={"extra-extra-large"} />
        </div>
      </div>
    )
  }

  return (
    <>
      {getLayout(<Component {...pageProps} />)}
      <SessionTimeoutModal
        open={isSessionTimeoutModalOpen}
        onStayLoggedIn={extendSessionAndCloseSessionTimeoutModal}
        isExtendingSession={isExtendingSession}
        onSignOut={signOutAndCloseSessionTimeoutModal}
        isSigningOut={isRevokingSession}
        onClose={() => {}}
        showErrorBanner={!!extendSessionError || !!revokeSessionError}
      />
      {isTimedOutModalOpen && <SessionTimeoutSignIn />}
    </>
  )
}

const queryClient = new QueryClient()

export default function MyApp(props: AppPropsWithLayout) {
  return (
    <>
      <Script src="/scripts/colorScheme.js" strategy="beforeInteractive" />
      <Script src="/scripts/loadIntercom.js" />
      <ErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <Provider store={store}>
            <PersistGate loading={null} persistor={persistor}>
              <PreviousRouteContextProvider>
                <ModalContextProvider>
                  <SidePanelContextProvider>
                    <App {...props} />
                  </SidePanelContextProvider>
                </ModalContextProvider>
              </PreviousRouteContextProvider>
            </PersistGate>
          </Provider>
        </QueryClientProvider>
      </ErrorBoundary>
    </>
  )
}
